import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@env/environment';
import { SessionService } from 'src/app/store/session/session.service';
import { SessionStore } from 'src/app/store/session/session.store';
import { getNewRelicAgent } from './newrelic.config';

const RESPONSE_BODY_MAX_LENGTH = 1000;

interface ApiErrorParams {
  endpointUrl: string;
  httpMethod?: string;
  httpStatus?: number;
  body?: unknown;
  errorMessage?: string;
  statusText?: string;
  pageOrigin?: string;
  pageHostname?: string;
  pageHref?: string;
  navigatorOnline?: boolean;
  errorEventType?: string;
  isAborted?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class NewRelicService {
  constructor(
    private sessionStore: SessionStore,
    private sessionService: SessionService,
    private router: Router,
  ) {}

  noticeError(error: Error | string, attributes: Record<string, unknown> = {}): void {
    if (!environment.newRelicEnabled) {
      return;
    }

    const agent = getNewRelicAgent();
    if (!agent) {
      return;
    }

    agent.noticeError(error, {
      ...this.getCommonAttributes(),
      ...attributes,
    });
  }
  

  reportApiError(params: ApiErrorParams): void {
    const error = new Error(`API ${params.httpMethod ?? ''} ${params.endpointUrl} -> ${params.httpStatus ?? ''}`.trim());
    error.name = 'ApiSupportError';

    this.noticeError(error, {
      endpoint: params.endpointUrl,
      httpMethod: params.httpMethod ?? '',
      httpStatus: params.httpStatus ?? '',
      responseBody: this.serializeResponseBody(params.body),
      errorMessage: params.errorMessage ?? '',
      statusText: params.statusText ?? '',
      pageOrigin: params.pageOrigin ?? '',
      pageHostname: params.pageHostname ?? '',
      pageHref: params.pageHref ?? '',
      navigatorOnline: params.navigatorOnline ?? '',
      errorEventType: params.errorEventType ?? '',
      isAborted: params.isAborted ?? false,
    });
  }

  private getCommonAttributes(): Record<string, unknown> {
    const userInfo = this.sessionStore.getValue().userInfo;
    const dealerSelected = this.sessionService.getDealerSelected();
    const oemDealerSelected = this.sessionService.getOemDealerSelected();

    return {
      userId: userInfo?.id ?? 'anonymous',
      userName: userInfo?.name ?? 'anonymous',
      userEmail: userInfo?.email ?? 'anonymous',
      currentRoute: this.router.url,
      url: window.location.href,
      dealerId: dealerSelected?.id ?? userInfo?.dealerId ?? '',
      oemDealerId: oemDealerSelected?.id ?? userInfo?.oemDealerId ?? '',
      environment: this.getEnvironmentName(),
    };
  }

  private getEnvironmentName(): string {
    if (environment.production) {
      return 'production';
    }

    if (environment.api.includes('api-stage')) {
      return 'stage';
    }

    return 'development';
  }

  private serializeResponseBody(errorBody: unknown): string {
    try {
      const serialized = JSON.stringify(errorBody) ?? '';
      return serialized.length > RESPONSE_BODY_MAX_LENGTH
        ? `${serialized.slice(0, RESPONSE_BODY_MAX_LENGTH)}...`
        : serialized;
    } catch {
      return '';
    }
  }
}
